import { useEffect, useState } from "react";
import { CalendarDays, MapPin, Users, Activity, AlertCircle } from "lucide-react";
import { useEventContent } from "@/context/EventContentContext";
import { api } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="card-cg p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="overline text-[var(--brand-muted)]">{label}</div>
      <Icon size={16} className="text-[var(--brand-primary)]" />
    </div>
    <div className="font-serif-cg text-2xl md:text-3xl mt-3 tracking-tight">{value}</div>
  </div>
);

export default function StaffDashboard() {
  const { event } = useEventContent();
  const [committees, setCommittees] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [incidents, setIncidents] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/committees"),
      api.get("/checklist"),
      api.get("/incidents"),
    ])
      .then(([c, ch, i]) => {
        setCommittees(c.data);
        setChecklist(ch.data);
        setIncidents(i.data);
      })
      .catch(() => {});
  }, []);

  const completedTasks = checklist.filter((t) => t.done).length;
  const openIncidents = incidents.filter((i) => i.status !== "Resuelto").length;

  return (
    <div data-testid="staff-dashboard" className="space-y-8">
      <div>
        <div className="overline text-[var(--brand-primary)]">Resumen general</div>
        <h2 className="font-serif-cg text-3xl md:text-4xl tracking-tight mt-2">Estado del evento</h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={CalendarDays} label="Fecha" value={event.date.split(",")[0]} testid="kpi-date" />
        <Stat icon={MapPin} label="Sede" value={event.venue} testid="kpi-venue" />
        <Stat icon={Users} label="Invitados estimados" value={event.expectedGuests} testid="kpi-guests" />
        <Stat icon={Activity} label="Estado" value={event.status} testid="kpi-status" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-cg p-6">
          <div className="overline text-[var(--brand-muted)]">Comités</div>
          <div className="font-serif-cg text-3xl mt-2">{committees.length}</div>
          <p className="text-xs text-[var(--brand-muted)] mt-1">
            {committees.filter((c) => c.status === "Completado").length} completados ·{" "}
            {committees.filter((c) => c.status === "En progreso").length} en curso
          </p>
        </div>
        <div className="card-cg p-6">
          <div className="overline text-[var(--brand-muted)]">Tareas operativas</div>
          <div className="font-serif-cg text-3xl mt-2">{completedTasks} / {checklist.length}</div>
          <div className="mt-3 h-1.5 bg-[var(--brand-surface-alt)] overflow-hidden">
            <div
              className="h-full bg-[var(--brand-primary)] transition-all"
              style={{ width: checklist.length ? `${(completedTasks / checklist.length) * 100}%` : "0%" }}
            />
          </div>
        </div>
        <div className="card-cg p-6">
          <div className="overline text-[var(--brand-muted)]">Incidencias abiertas</div>
          <div className="font-serif-cg text-3xl mt-2">{openIncidents}</div>
          <p className="text-xs text-[var(--brand-muted)] mt-1">
            Total reportadas: {incidents.length}
          </p>
        </div>
      </div>

      <div className="card-cg p-6">
        <div className="flex items-center gap-2">
          <AlertCircle className="text-[var(--brand-accent)]" size={16} />
          <div className="overline text-[var(--brand-primary)]">Anuncios urgentes</div>
        </div>
        <ul className="mt-4 space-y-3 text-sm">
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-accent)] mt-2" />
            <span>Confirmar montaje final el día previo al evento (T-1).</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-accent)] mt-2" />
            <span>Cierre de pagos a proveedores con anticipo pendiente esta semana.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-accent)] mt-2" />
            <span>Briefing general con voluntarios y edecanes 2 horas antes del evento.</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
